(function () {
  // Theme preview proxies CDN scripts through the device's current origin.
  // Load the bundled metadata from the same asset directory and proxy.
  const registryScriptUrl = document.currentScript && document.currentScript.src;
  const localAssetUrl = (url) => {
    if (!url || !registryScriptUrl) return url;
    try {
      const configured = new URL(url, window.location.href);
      const script = new URL(registryScriptUrl, window.location.href);
      const directory = script.pathname.slice(0, script.pathname.lastIndexOf('/') + 1);
      if (script.origin === window.location.origin
        && script.pathname.endsWith('/assets/poptex-content-registry.js')
        && [directory,directory.replace(/^\/cdn\//,'/')].some(path => configured.pathname === path + 'poptex-metadata.json')) {
        configured.pathname = directory + 'poptex-metadata.json';
        configured.protocol = script.protocol;
        configured.host = script.host;
      }
      return configured.href;
    } catch (_) { return url; }
  };
  const DEFAULT_CONFIG = {
    localUrl: '',
    latestUrl: 'https://assets.poptex.com/meta/latest.json',
    cacheKey: 'poptex.content_metadata.v2.1',
    reloadKey: 'poptex.content_metadata_reloaded_version.v2',
    schemaMajor: 2,
    timeoutMs: 5000,
    syncTtlMs: 60000,
    autoRefreshOnRemoteUpdate: false,
    refreshDelayMs: 150
  };

  const state = {
    config: Object.assign({}, DEFAULT_CONFIG),
    snapshot: null,
    expanded: null,
    latest: null,
    source: '',
    status: 'idle',
    lastError: '',
    lastSyncAt: 0,
    lastSuccessAt: 0,
    retryAt: 0,
    failures: 0,
    timer: null,
    suspended: false,
    storageKey: '',
    initPromise: null,
    syncPromise: null,
    listeners: []
  };

  const clone = (value) => {
    if (value === undefined || value === null) return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_error) {
      return value;
    }
  };

  const majorVersion = (value) => {
    const major = Number(String(value || '').split('.')[0]);
    return Number.isFinite(major) ? major : 0;
  };

  // Keep cached transport compact; resolve URLs and styles at read boundaries.
  const expandSnapshot = (input) => {
    if (!input || !['2.1.0', '2.2.0'].includes(input.schemaVersion)) return input;
    const assets = new Map(input.assets.map(asset => [asset.id, asset.url]));
    if (assets.size !== input.assets.length || input.assets.some(asset => !asset.id || typeof asset.url !== 'string' || !asset.url)) throw new Error('Invalid metadata asset index');
    const expand = value => {
      if (Array.isArray(value)) return value.map(expand);
      if (!value || typeof value !== 'object') return value;
      const output = {};
      for (const [key, item] of Object.entries(value)) {
        output[key] = expand(item);
        if (key === 'assetId' || key.endsWith('AssetId')) {
          if (item && !assets.has(item)) throw new Error('Missing metadata asset: ' + item);
          output[key === 'assetId' ? 'url' : key.slice(0, -7) + 'Url'] = item ? assets.get(item) : null;
        }
      }
      return output;
    };
    const productSeries = input.productSeries.map(style => {
      const record = expand(style);
      return { ...record, coverUrl: record.effectUrl ?? null };
    });
    const styles = new Map(productSeries.map(style => [style.id, style]));
    return { ...input, productSeries,
      productSeriesGroups: input.productSeriesGroups.map(group => {
        if (!Array.isArray(group.styleIds)) throw new Error('Missing metadata style references');
        const { styleIds, ...record } = expand(group);
        return { ...record, styles: styleIds.map(id => {
          if (!styles.has(id)) throw new Error('Missing metadata style: ' + id);
          return styles.get(id);
        }) };
      }),
      patterns: input.patterns.map(pattern => {
        if (!styles.has(pattern.productSeriesId)) throw new Error('Missing pattern style');
        const record = expand(pattern);
        const previewImages = { display: null, ...Object.fromEntries(pattern.assets.map(asset => [asset.role, assets.get(asset.assetId)])) };
        return { ...record, assets: pattern.assets, previewImages, effectUrl: previewImages.mainEffect ?? null, sourceArtworkUrl: previewImages.sourceArtwork ?? null };
      }),
      referencePresets: expand(input.referencePresets)
    };
  };

  const isValidSnapshot = (value) => {
    if (!value || typeof value !== 'object') return false;
    if (majorVersion(value.schemaVersion) !== state.config.schemaMajor || !['2.0.0', '2.1.0', '2.2.0'].includes(value.schemaVersion)) return false;
    if (!(Array.isArray(value.patterns) && Array.isArray(value.productSeries)
      && Array.isArray(value.productSeriesGroups) && Array.isArray(value.referencePresets) && Array.isArray(value.assets)
      && [value.assets, value.referencePresets, value.productSeries, value.productSeriesGroups, value.patterns].every(records => records.every(record => record && typeof record === 'object' && !Array.isArray(record)))
      && value.patterns.every(p => p.productSeriesId && !Object.hasOwn(p, 'productSeriesIds')))) return false;
    if (value.schemaVersion === '2.0.0') return true;
    const assets = new Set(value.assets.map(asset => asset?.id));
    const styles = new Set(value.productSeries.map(style => style?.id));
    if (assets.size !== value.assets.length || value.assets.some(asset => !asset?.id || typeof asset.url !== 'string' || !asset.url)) return false;
    const referencesValid = item => {
      if (Array.isArray(item)) return item.every(referencesValid);
      if (!item || typeof item !== 'object') return true;
      return Object.entries(item).every(([key, child]) =>
        (!(key === 'assetId' || key.endsWith('AssetId')) || !child || assets.has(child)) && referencesValid(child));
    };
    return value.productSeriesGroups.every(group => Array.isArray(group?.styleIds) && group.styleIds.every(id => styles.has(id)))
      && value.patterns.every(pattern => styles.has(pattern.productSeriesId) && Array.isArray(pattern.assets)
        && pattern.assets.every(asset => asset && typeof asset === 'object' && !Array.isArray(asset)))
      && [value.productSeries, value.productSeriesGroups, value.patterns, value.referencePresets].every(referencesValid);
  };

  const freeze = value => {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
      Object.values(value).forEach(freeze);
      Object.freeze(value);
    }
    return value;
  };
  const sourceUrl = () => new URL(state.config.latestUrl || state.config.localUrl || '/', window.location.href).href;
  const readStored = key => {
    try { return JSON.parse(window.localStorage.getItem(key) || 'null'); } catch (_) { return null; }
  };
  const saveStored = (key, value) => {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };
  const readClock = () => {
    const clock = readStored(state.storageKey + '.sync');
    const now = Date.now();
    if (clock && Number.isFinite(clock.successAt) && clock.successAt <= now && clock.successAt > state.lastSuccessAt) {
      state.lastSuccessAt = clock.successAt;
      state.retryAt = 0; state.failures = 0;
    }
  };

  const hasSameContent = (first, second) => {
    if (!first || !second) return false;
    if (first.contentHash && second.contentHash) return first.contentHash === second.contentHash;
    return Boolean(first.contentVersion && first.contentVersion === second.contentVersion);
  };

  const isNewerOrSameSnapshot = (incoming, current) => {
    if (!current) return true;
    const prepared = value => value?.source === 'LOCAL_V2_PREPARATION' || value?.source?.type === 'LOCAL_V2_PREPARATION';
    if (prepared(current) && !prepared(incoming)) return true;
    if (!prepared(current) && prepared(incoming)) return false;
    if (hasSameContent(incoming, current)) return true;
    const incomingUpdatedAt = Date.parse(incoming && (incoming.contentUpdatedAt || incoming.updatedAt) || '');
    const currentUpdatedAt = Date.parse(current && (current.contentUpdatedAt || current.updatedAt) || '');
    if (!Number.isFinite(incomingUpdatedAt)) return false;
    if (!Number.isFinite(currentUpdatedAt)) return true;
    if (incomingUpdatedAt === currentUpdatedAt && incoming.schemaVersion < current.schemaVersion) return false;
    return incomingUpdatedAt >= currentUpdatedAt;
  };

  const readCache = () => {
    try {
      const raw = window.localStorage.getItem(state.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  };

  const writeCache = (snapshot) => {
    try {
      window.localStorage.setItem(state.storageKey, JSON.stringify(snapshot));
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes or when quota is full.
    }
  };

  const notify = () => {
    state.listeners.slice().forEach((listener) => {
      try {
        listener(getSnapshot());
      } catch (error) {
        console.warn('POPTEX content registry listener failed:', error);
      }
    });
  };

  const dispatchUpdateEvent = (snapshot, source) => {
    try {
      window.dispatchEvent(new CustomEvent('poptex:metadata-updated', {
        detail: {
          source,
          contentVersion: snapshot && snapshot.contentVersion,
          contentHash: snapshot && snapshot.contentHash
        }
      }));
    } catch (_error) {
      // CustomEvent can be unavailable in very old browsers.
    }
  };

  const setStatus = (status, error) => {
    state.status = status;
    state.lastError = error ? String(error.message || error) : '';
  };

  const setSnapshot = (snapshot, source, persist) => {
    if (!isValidSnapshot(snapshot)) return false;
    if (hasSameContent(state.snapshot, snapshot)) return true;
    let expanded;
    try { expanded = freeze(expandSnapshot(clone(snapshot))); } catch (_) { return false; }
    state.snapshot = snapshot;
    state.expanded = expanded;
    state.source = source || '';
    if (persist) writeCache(snapshot);
    notify();
    dispatchUpdateEvent(snapshot, source);

    return true;
  };

  const fetchJson = async (url, timeoutMs, cache = 'default') => {
    if (!url) return null;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs || state.config.timeoutMs);
    try {
      const response = await fetch(url, {
        credentials: 'omit',
        cache,
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      return await response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const resolveSnapshotUrl = (latest) => {
    if (!latest || typeof latest !== 'object') return '';
    if (latest.url) { try { return new URL(latest.url, state.config.latestUrl).href; } catch (_) { return ''; } }
    if (latest.contentVersion) {
      try {
        return new URL(`versions/${encodeURIComponent(latest.contentVersion)}.json`, state.config.latestUrl).toString();
      } catch (_error) {
        return '';
      }
    }
    return '';
  };

  const visible = () => !state.suspended && !document.hidden;
  const schedule = () => {
    window.clearTimeout(state.timer);
    if (!visible() || !state.config.latestUrl || !document.addEventListener) return;
    const due = state.retryAt || (state.lastSuccessAt ? state.lastSuccessAt + state.config.syncTtlMs : Date.now());
    state.timer = window.setTimeout(() => { void syncRemote(); }, Math.max(0, due - Date.now()));
  };
  const syncRemote = (options) => {
    const syncOptions = options || {};
    if (!state.config.latestUrl || (!syncOptions.force && !visible())) return Promise.resolve(getSnapshot());
    if (state.syncPromise) return state.syncPromise;
    const execute = async () => {
      if (!syncOptions.force && !visible()) return getSnapshot();
      const cached = readCache();
      if (isValidSnapshot(cached) && isNewerOrSameSnapshot(cached, state.snapshot)) setSnapshot(cached, 'browser-cache', false);
      readClock();
      const now = Date.now();
      if (!syncOptions.force && (state.retryAt ? now < state.retryAt : (state.snapshot && state.lastSuccessAt && now - state.lastSuccessAt < state.config.syncTtlMs))) return getSnapshot();
      state.lastSyncAt = now;
      return fetchJson(state.config.latestUrl, state.config.timeoutMs, 'no-cache')
      .then((latest) => {
        state.latest = latest || null;
        if (!latest || !latest.contentVersion) throw new Error('Invalid metadata pointer');
        if (!['2.0.0', '2.1.0', '2.2.0'].includes(latest.schemaVersion) || majorVersion(latest.schemaVersion) !== state.config.schemaMajor) throw new Error('Unsupported metadata schema');
        if (state.snapshot && latest.contentHash && state.snapshot.contentHash === latest.contentHash) return state.snapshot;
        if (state.snapshot && !latest.contentHash && latest.contentVersion === state.snapshot.contentVersion) return state.snapshot;
        return fetchJson(resolveSnapshotUrl(latest), state.config.timeoutMs).then(async snapshot => {
          if (!isValidSnapshot(snapshot) || snapshot.contentHash !== latest.contentHash || snapshot.contentVersion !== latest.contentVersion || snapshot.schemaVersion !== latest.schemaVersion) throw new Error('Metadata pointer does not match its snapshot');
          if (globalThis.crypto?.subtle) {
            const source={...(snapshot.generation ? {generation:snapshot.generation} : {}),...(snapshot.colorFamilies ? {colorFamilies:snapshot.colorFamilies} : {}),assets:snapshot.assets,referencePresets:snapshot.referencePresets,productSeries:snapshot.productSeries,productSeriesGroups:snapshot.productSeriesGroups,patterns:snapshot.patterns};
            const digest=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(JSON.stringify(source)));
            const hash=Array.from(new Uint8Array(digest),byte=>byte.toString(16).padStart(2,'0')).join('');
            if(hash!==snapshot.contentHash)throw new Error('Metadata content hash mismatch');
          }
          return snapshot;
        });
      })
      .then((snapshot) => {
        if (snapshot && isNewerOrSameSnapshot(snapshot, state.snapshot)) setSnapshot(snapshot, 'r2', true);
        state.lastSuccessAt = Date.now();
        state.failures = 0;
        state.retryAt = 0;
        saveStored(state.storageKey + '.sync', { successAt: state.lastSuccessAt });
        setStatus('ready', '');
        return getSnapshot();
      })
      .catch((error) => {
        console.warn('POPTEX metadata sync failed:', error);
        setStatus('remote-error', error);
        state.failures++;
        state.retryAt = Date.now() + Math.min(60000, 15000 * 2 ** Math.min(2, state.failures - 1));
        return getSnapshot();
      });
    };
    const locks = window.navigator?.locks;
    state.syncPromise = Promise.resolve().then(() => locks ? locks.request(state.storageKey + '.lock', execute) : execute())
      .catch(error => {
        setStatus('remote-error', error);
        state.retryAt = Date.now() + 15000;
        return getSnapshot();
      }).finally(() => { state.syncPromise = null; schedule(); });
    return state.syncPromise;
  };

  const refreshRemote = (options) => {
    window.setTimeout(() => {
      syncRemote(options).catch(() => {});
    }, 0);
    return getSnapshot();
  };

  const init = (inputConfig) => {
    if (state.initPromise) return state.initPromise;
    state.config = Object.assign({}, state.config, inputConfig || {});
    state.config.syncTtlMs = Math.max(1000, Number(state.config.syncTtlMs) || 60000);
    state.storageKey = state.config.cacheKey + '.source:' + encodeURIComponent(sourceUrl());
    if (!readCache() && sourceUrl() === DEFAULT_CONFIG.latestUrl) {
      const legacy = readStored(state.config.cacheKey);
      const base = legacy?.source?.publicBaseUrl;
      if (isValidSnapshot(legacy) && (!base || String(base).replace(/\/+$/, '') === 'https://assets.poptex.com')) writeCache(legacy);
    }
    const cached = readCache();
    if (isValidSnapshot(cached)) setSnapshot(cached, 'browser-cache', false);
    readClock();
    const localPromise = fetchJson(localAssetUrl(state.config.localUrl), state.config.timeoutMs)
      .then((localSnapshot) => {
        if (!isValidSnapshot(localSnapshot)) throw new Error('Invalid local metadata');
        if (localSnapshot && isNewerOrSameSnapshot(localSnapshot, state.snapshot)) {
          setSnapshot(localSnapshot, 'shopify-local', true);
        }
        setStatus('ready', '');
        schedule();
        if (!document.addEventListener) window.setTimeout(syncRemote, 0);
        return getSnapshot();
      })
      .catch(async (error) => {
        console.warn('POPTEX local metadata load failed:', error);
        setStatus(state.snapshot ? 'ready' : 'local-error', error);
        if (!state.snapshot) await syncRemote({ force: true });
        else schedule();
        return getSnapshot();
      });

    state.initPromise = state.snapshot ? Promise.resolve(getSnapshot()) : localPromise;
    if (state.snapshot) schedule();
    return state.initPromise;
  };

  const getSnapshot = () => clone(state.expanded);

  const byIdentity = (records, value) => {
    const key = String(value || '');
    return records.find((record) => record && [record.id, record.slug, record.handle].map(String).includes(key)) || null;
  };

  const list = (key) => {
    const snapshot = state.expanded || {};
    return Array.isArray(snapshot[key]) ? clone(snapshot[key]).sort((a, b) => (Number(b?.sortOrder) || 0) - (Number(a?.sortOrder) || 0)) : [];
  };

  const listScenes = () => init().then(() => list('scenes'));
  const getScene = (id) => listScenes().then((records) => byIdentity(records, id));

  const listEvents = () => init().then(() => list('events'));
  const getEvent = (id) => listEvents().then((records) => byIdentity(records, id));

  const listSceneEvents = (sceneId) => Promise.all([getScene(sceneId), listEvents()])
    .then(([scene, events]) => {
      const ids = new Set([sceneId, scene && scene.id, scene && scene.slug].filter(Boolean).map(String));
      return events.filter((event) => ids.has(String(event.sceneId || '')) || ids.has(String(event.sceneSlug || '')));
    });

  const listTemplates = (query) => init().then(() => {
    const filters = query || {};
    return list('templates').filter((template) => {
      if (filters.eventId && String(template.eventId || '') !== String(filters.eventId)) return false;
      if (filters.sceneId && String(template.sceneId || '') !== String(filters.sceneId)) return false;
      return true;
    });
  });

  const hasColorCatalog = snapshot => {
    const codes = ['BLACK_GRAY','WHITE_BEIGE','BROWN','RED_PINK','ORANGE_YELLOW','GREEN','BLUE','PURPLE'];
    if (snapshot?.schemaVersion !== '2.2.0' || snapshot.colorFamilies?.length !== 8) return false;
    const orders = [];
    if (!snapshot.colorFamilies.every((f,i) => f?.code === codes[i] && f.samples?.length === 1 && /^#[0-9a-f]{6}$/i.test(f.samples[0]) && Array.isArray(f.classificationSamples) && f.classificationSamples.length && f.classificationSamples.every(r => {
      orders.push(r?.order); return /^#[0-9a-f]{6}$/i.test(r?.hex) && Number.isInteger(r?.order);
    }))) return false;
    return orders.sort((a,b)=>a-b).join(',') === '0,1,2,3,4,5,6,7,8,9,10,11'
      && snapshot.patterns.every(p => Array.isArray(p.colors) && p.colors.every(c => c?.family == null || codes.includes(c.family)));
  };
  const listPatterns = (query) => init().then(() => {
    const selected = String(query?.colorFamilies || '').split(',').filter(Boolean);
    const allowed = ['BLACK_GRAY','WHITE_BEIGE','BROWN','RED_PINK','ORANGE_YELLOW','GREEN','BLUE','PURPLE','UNCLASSIFIED'];
    if (selected.some(code => !allowed.includes(code))) throw new Error('Invalid color family');
    if (selected.length && !hasColorCatalog(state.snapshot)) throw new Error('Color catalog requires refresh');
    return list('patterns').filter(p => (!query?.productSeriesId || p.productSeriesId === query.productSeriesId)
      && (!selected.length || selected.some(code => code === 'UNCLASSIFIED'
        ? !p.colors?.length || p.colors.length > 4 || p.colors.some(c => !c?.name?.trim() || !/^#[0-9a-f]{6}$/i.test(c.hex || '') || !allowed.slice(0,8).includes(c.family))
        : p.colors?.some(c => c.family === code)))
      && (!query?.search || (p.name + ' ' + p.slug).toLowerCase().includes(query.search.toLowerCase()))
      && (query?.isNew === undefined || p.isNew === (query.isNew === true || query.isNew === 'true')));
  });
  const getPattern = (id) => listPatterns().then(records => byIdentity(records,id));
  const getTemplate = getPattern;

  const listEventTemplates = (eventId) => Promise.all([getEvent(eventId), listTemplates()])
    .then(([event, templates]) => {
      const ids = new Set([eventId, event && event.id, event && event.slug].filter(Boolean).map(String));
      return templates.filter((template) => ids.has(String(template.eventId || '')) || ids.has(String(template.eventSlug || '')));
    });

  const listAssets = () => init().then(() => list('assets'));
  const getAsset = (id) => listAssets().then((records) => byIdentity(records, id));
  const listReferencePresets = (query) => init().then(() => {
    const filters = query || {};
    return list('referencePresets').filter((preset) => {
      if (filters.type && String(preset.type || '') !== String(filters.type)) return false;
      return true;
    });
  });
  const listProductSeries = (query) => init().then(() => list('productSeries').filter((series) => {
    if (query?.productType && series.productType !== query.productType) return false;
    if (query?.tier && series.tier !== query.tier) return false;
    return true;
  }));
  const listProductSeriesGroups = (query) => init().then(() => list('productSeriesGroups').filter(group => {
    if (query?.tier && group.tier !== query.tier) return false;
    if (query?.productType && !group.productTypes?.includes(query.productType)) return false;
    return true;
  }));
  const getProductSeriesGroup = id => listProductSeriesGroups().then(records => byIdentity(records,id));
  const getProductSeries = (id) => listProductSeries().then((records) => byIdentity(records, id));
  const listProductSeriesPatterns = (id, query) => listPatterns({ ...query, productSeriesId: id });

  const onChange = (listener) => {
    if (typeof listener !== 'function') return () => {};
    state.listeners.push(listener);
    return () => {
      state.listeners = state.listeners.filter((item) => item !== listener);
    };
  };

  window.PoptexContentRegistry = {
    init,
    sync: syncRemote,
    refresh: refreshRemote,
    getSnapshot,
    getReadonlySnapshot: () => state.expanded,
    getCacheKey: () => state.storageKey,
    getSyncState: () => ({ attemptedAt: state.lastSyncAt, successAt: state.lastSuccessAt, retryAt: state.retryAt }),
    getLatest: () => clone(state.latest),
    getSource: () => state.source,
    getStatus: () => state.status,
    getLastError: () => state.lastError,
    hasSnapshot: () => Boolean(state.snapshot),
    onChange,
    listScenes,
    getScene,
    listEvents,
    getEvent,
    listSceneEvents,
    listPatterns,
    getPattern,
    listTemplates,
    getTemplate,
    listEventTemplates,
    listAssets,
    getAsset,
    listReferencePresets,
    listProductSeriesGroups,
    getProductSeriesGroup,
    listProductSeries,
    getProductSeries,
    listProductSeriesPatterns
  };

  document.addEventListener?.('visibilitychange', schedule);
  window.addEventListener?.('pagehide', () => { state.suspended = true; window.clearTimeout(state.timer); });
  window.addEventListener?.('pageshow', () => { state.suspended = false; schedule(); });
  window.addEventListener?.('storage', event => {
    if (event.key !== state.storageKey && event.key !== state.storageKey + '.sync') return;
    if (event.key === state.storageKey) {
      const cached = readCache();
      if (isValidSnapshot(cached) && isNewerOrSameSnapshot(cached, state.snapshot)) setSnapshot(cached, 'browser-cache', false);
    }
    readClock();
    schedule();
  });
  init(window.POPTEX_METADATA_CONFIG || {});
})();
